<template>
  <Menu as="div" class="relative inline-block text-left">
    <div>
      <MenuButton
        class="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-200"
        aria-label="Notifications"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-6 h-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
          />
        </svg>
        <span
          v-if="unseenCount > 0"
          class="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full"
        >
          {{ unseenCount > 9 ? '9+' : unseenCount }}
        </span>
      </MenuButton>
    </div>

    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <MenuItems
        class="fixed sm:absolute right-4 sm:right-auto left-4 sm:left-0 mt-2 w-auto sm:w-96 origin-top-right sm:origin-top-left rounded-lg bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-600 focus:outline-none z-50 max-h-[500px] flex flex-col"
        style="box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)"
      >
        <div class="px-3 sm:px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center gap-2">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">الإشعارات</h3>
          <button
            v-if="unseenCount > 0"
            @click="markAllAsRead"
            class="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
          >
            تعليم الكل كمقروء
          </button>
        </div>

        <div
          ref="scrollContainer"
          class="overflow-y-auto flex-1"
          style="max-height: 400px"
        >
          <div v-if="isLoading" class="p-4 text-center text-gray-500 dark:text-gray-400">
            جاري التحميل...
          </div>

          <div v-else-if="isError" class="p-4 text-center text-red-500 dark:text-red-400">
            حدث خطأ في تحميل الإشعارات
          </div>

          <div v-else-if="allNotifications.length === 0" class="p-4 text-center text-gray-500 dark:text-gray-400">
            لا توجد إشعارات
          </div>

          <div v-else>
            <MenuItem
              v-for="notification in allNotifications"
              :key="notification.id"
              v-slot="{ active }"
            >
              <button
                @click="handleNotificationClick(notification)"
                :class="[
                  active ? 'bg-gray-50 dark:bg-gray-700/50' : '',
                  !notification.is_seen ? 'bg-blue-50 dark:bg-blue-900/20 shadow-sm' : '',
                  'w-full text-right px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-150'
                ]"
                :style="!notification.is_seen ? 'box-shadow: inset 2px 0 0 0 rgb(59 130 246 / 0.5)' : ''"
              >
                <div class="flex items-start gap-2 sm:gap-3">
                  <div
                    v-if="!notification.is_seen"
                    class="w-2 h-2 mt-1 sm:mt-1.5 rounded-full bg-blue-500 flex-shrink-0"
                  ></div>
                  <div v-else class="w-2 flex-shrink-0"></div>

                  <div class="flex-1 min-w-0">
                    <p class="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">
                      {{ notification.subject }}
                    </p>
                    <p class="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2 break-words">
                      {{ notification.content }}
                    </p>
                    <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {{ formatDate(notification.date) }}
                    </p>
                  </div>
                </div>
              </button>
            </MenuItem>

            <div v-if="isFetchingNextPage" class="p-4 text-center text-gray-500 dark:text-gray-400">
              جاري التحميل...
            </div>

            <div v-else-if="!hasNextPage" class="p-3 text-center text-xs text-gray-400 dark:text-gray-500">
              لا يوجد المزيد من الإشعارات
            </div>
          </div>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { useInfiniteQuery, useQueryClient } from '@tanstack/vue-query'
import { useInfiniteScroll } from '@vueuse/core'
import { fetchNotifications, markNotificationAsSeen } from '@/services/notifications'
import type { Notification } from '@/types/global'

const limit = 10
const queryClient = useQueryClient()
const scrollContainer = ref<HTMLElement | null>(null)

const fetchNotificationsPage = async ({ pageParam = 1 }) => {
  const notifications = await fetchNotifications(pageParam, limit)
  return {
    notifications,
    nextPage: notifications.length === limit ? pageParam + 1 : undefined
  }
}

const {
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  isError
} = useInfiniteQuery({
  queryKey: ['notifications'],
  queryFn: fetchNotificationsPage,
  getNextPageParam: (lastPage) => lastPage.nextPage,
  initialPageParam: 1
})

const allNotifications = computed(() => {
  if (!data.value) return []
  return data.value.pages.flatMap((page) => page.notifications)
})

const unseenCount = computed(() => {
  return allNotifications.value.filter((n) => !n.is_seen).length
})

// Setup infinite scroll
useInfiniteScroll(
  scrollContainer,
  () => {
    if (hasNextPage.value && !isFetchingNextPage.value) {
      fetchNextPage()
    }
  },
  {
    distance: 50,
    canLoadMore: () => hasNextPage.value ?? false
  }
)

const handleNotificationClick = async (notification: Notification) => {
  // Mark as seen if not already seen
  if (!notification.is_seen) {
    try {
      await markNotificationAsSeen(notification.id)
      notification.is_seen = true

      // Invalidate query to refetch and update cache
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    } catch (error) {
      console.error('Failed to mark notification as seen:', error)
    }
  }

  // TODO: Handle navigation based on action, res_model, res_id
  handleNotificationAction(notification)
}

const handleNotificationAction = (notification: Notification) => {
  // TODO: Implement navigation logic based on:
  // - notification.action
  // - notification.res_model
  // - notification.res_id
  console.log('Notification clicked:', notification)
}

const markAllAsRead = async () => {
  const unseenNotifications = allNotifications.value.filter((n) => !n.is_seen)

  for (const notification of unseenNotifications) {
    try {
      await markNotificationAsSeen(notification.id)
      notification.is_seen = true
    } catch (error) {
      console.error('Failed to mark notification as seen:', error)
    }
  }

  // Invalidate query to refetch and update cache
  queryClient.invalidateQueries({ queryKey: ['notifications'] })
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'الآن'
  if (diffMins < 60) return `منذ ${diffMins} دقيقة`
  if (diffHours < 24) return `منذ ${diffHours} ساعة`
  if (diffDays < 7) return `منذ ${diffDays} يوم`

  return date.toLocaleDateString('ar-SA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}
</script>
